import { useEffect } from "react";
import { supabase } from "./lib/supabase";
import { qualificationPositionCovered, qualificationPositionRequirement } from "./qualification-position-coverage";

const norm=(value:string|null|undefined)=>String(value||"").toLowerCase().replace(/[^a-z0-9]/g,"");
const activeOn=(date:string|null|undefined)=>!date||new Date(date+"T23:59:59").getTime()>=Date.now();

type Qualification={
  position:string|null;
  process:string|null;
  qualified_processes:string[]|null;
  process_mode:string|null;
  qualification_scope:string|null;
  material_division:string|null;
  expiry_date:string|null;
};
type Welder={employee_number:string|null;qualifications:Qualification[]};

function selectedValue(root:HTMLElement,labelText:string){
  const label=[...root.querySelectorAll<HTMLLabelElement>("label")].find(x=>(x.textContent||"").toLowerCase().includes(labelText.toLowerCase()));
  return label?.querySelector<HTMLSelectElement>("select")?.value||"";
}
function scopeMatches(q:Qualification,scope:string){
  const stored=scope==="w471"||scope==="w472"?"standard":scope;
  const division=scope==="w472"?"aluminum":"steel";
  return norm(q.qualification_scope||"standard")===norm(stored)&&norm(q.material_division)===division;
}
function processMatches(q:Qualification,process:string){
  return q.qualified_processes?.length?q.qualified_processes.some(x=>norm(x)===norm(process)):norm(q.process)===norm(process);
}

export default function EligibilityPositionGuard(){
 useEffect(()=>{
  let timer:number|undefined;
  let request=0;
  const apply=async()=>{
    const root=document.querySelector<HTMLElement>(".eligibility-page");
    if(!root)return;
    const required=selectedValue(root,"Required position");
    const process=selectedValue(root,"Process");
    const mode=selectedValue(root,"Process mode");
    const scope=selectedValue(root,"Certification program");
    if(!required||!process||!scope)return;
    const cards=[...root.querySelectorAll<HTMLElement>(".eligibility-candidates .candidate-card")].filter(card=>card.querySelector(".decision.yes")?.textContent?.toUpperCase().includes("QUALIFIED"));
    if(!cards.length)return;
    const ids=cards.map(card=>((card.textContent||"").match(/Welder ID\s+([^\s]+)/i)?.[1]||"").trim()).filter(Boolean);
    if(!ids.length)return;
    const current=++request;
    const {data,error}=await supabase.from("welders").select("employee_number,qualifications(position,process,qualified_processes,process_mode,qualification_scope,material_division,expiry_date)").in("employee_number",ids).eq("active",true).is("archived_at",null);
    if(error||current!==request)return;
    const map=new Map<string,Welder>((data as Welder[]||[]).map(w=>[String(w.employee_number||""),w]));
    let visible=0;
    for(const card of cards){
      const id=((card.textContent||"").match(/Welder ID\s+([^\s]+)/i)?.[1]||"").trim();
      const welder=map.get(id);
      const good=Boolean(welder?.qualifications?.some(q=>activeOn(q.expiry_date)&&scopeMatches(q,scope)&&processMatches(q,process)&&(!mode||!q.process_mode||norm(q.process_mode)===norm(mode))&&qualificationPositionCovered(q.position,required)));
      card.style.display=good?"":"none";
      card.dataset.positionSequenceValid=good?"true":"false";
      if(good)visible++;
    }
    const decisions=[...root.querySelectorAll<HTMLElement>(".eligibility-result > .decision")];
    const welderDecision=decisions.find(x=>x.querySelector("h2")?.textContent?.toLowerCase().includes("qualified welder"));
    if(welderDecision){
      const count=welderDecision.querySelector<HTMLElement>(":scope > span");if(count)count.textContent=String(visible);
      const heading=welderDecision.querySelector("h2");if(heading)heading.textContent=visible===1?"Qualified welder found":`${visible} qualified welders found`;
      welderDecision.classList.toggle("yes",visible>0);welderDecision.classList.toggle("no",visible===0);
      const p=welderDecision.querySelector("p");
      if(p&&visible===0)p.textContent=`No welder qualification covers the complete position sequence required for ${required}. Required sequence: ${qualificationPositionRequirement(required)}.`;
    }
  };
  const schedule=()=>{window.clearTimeout(timer);timer=window.setTimeout(()=>void apply(),80)};
  const observer=new MutationObserver(schedule);observer.observe(document.body,{childList:true,subtree:true});
  document.addEventListener("change",schedule,true);document.addEventListener("input",schedule,true);schedule();
  return()=>{window.clearTimeout(timer);observer.disconnect();document.removeEventListener("change",schedule,true);document.removeEventListener("input",schedule,true)};
 },[]);
 return null;
}
